import { DemoAlarmSituation } from '@hakimo-ui/hakimo/types';
import { MultiSelect } from '@hakimo-ui/hakimo/ui-elements';
import { useCallback } from 'react';

interface Props {
  situations: DemoAlarmSituation[];
  isLoading: boolean;
  value: DemoAlarmSituation[];
  onChange: (next: DemoAlarmSituation[]) => void;
}

const labelClass =
  'text-onlight-text-2 dark:text-ondark-text-2 mb-1 block text-sm';

interface ChipProps {
  text: string;
  onRemove: () => void;
}

/**
 * Mirrors the shared `Label` chip, but capped at `max-w-full` with the text
 * truncating inside it. Situation names run long enough to overflow the field
 * otherwise, and `Label` exposes no way to bound its own width.
 */
function SituationChip({ text, onRemove }: ChipProps) {
  return (
    <span className="inline-flex max-w-full items-center rounded-3xl bg-gray-200 px-3.5 py-0.5 text-sm font-medium text-gray-800 dark:bg-gray-200/10 dark:text-gray-400">
      <span className="min-w-0 truncate" title={text}>
        {text}
      </span>
      <button
        type="button"
        onClick={onRemove}
        className="hover:text-status-red ml-0.5 inline-flex flex-shrink-0 items-center justify-center rounded-full p-1 focus:outline-none"
      >
        <span className="sr-only">remove option</span>
        <svg
          className="h-2.5 w-2.5"
          stroke="currentColor"
          fill="none"
          viewBox="0 0 8 8"
        >
          <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
        </svg>
      </button>
    </span>
  );
}

export function SituationsField(props: Props) {
  const { situations, isLoading, value, onChange } = props;

  const getItems = useCallback(
    (query: string) =>
      situations.filter((s) =>
        s.situation.toLowerCase().includes(query.toLowerCase())
      ),
    [situations]
  );

  if (isLoading) {
    return (
      <div>
        <span className={labelClass}>Ingest by situations</span>
        <div className="border-onlight-line-3 bg-onlight-bg-2 dark:border-ondark-line-3 dark:bg-ondark-bg-2 h-9 w-full animate-pulse rounded border" />
      </div>
    );
  }

  return (
    <div>
      <span className={labelClass}>Ingest by situations</span>
      <MultiSelect
        value={value}
        onChange={onChange}
        displayValue={(s) => s.situation}
        getItems={getItems}
        id={(s) => s.situation}
        customSelectedRenderer={(selected) => (
          <>
            {selected.map((s, idx) => (
              <SituationChip
                key={s.situation}
                text={s.situation}
                onRemove={() =>
                  onChange([
                    ...selected.slice(0, idx),
                    ...selected.slice(idx + 1),
                  ])
                }
              />
            ))}
          </>
        )}
      />
    </div>
  );
}
